import { useState } from 'react';
import { getCoinColor } from '../services/coinColorService';
import { getIconUrl } from '../services/iconService';

/**
 * CurrencyIcon Component
 * 
 * Displays cryptocurrency icons using the internal API endpoint or external URL.
 * Falls back to a colored circle with ticker initial if the icon fails to load.
 * 
 * @param {Object} props
 * @param {Object} props.currency - The currency object with id, ticker, name, image
 * @param {string} props.size - Size variant: 'xs', 'sm', 'md', 'lg', 'xl'
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.lazy - Whether to lazy load the image
 * @param {string} props.externalImage - External image URL (from CoinGecko API)
 */
export const CurrencyIcon = ({ 
  currency, 
  size = 'md', 
  className = '',
  lazy = true,
  externalImage = null
}) => {
  const [error, setError] = useState(false);
  
  const sizeClasses = {
    xs: 'w-4 h-4 text-[8px]',
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
    lg: 'w-8 h-8 text-sm',
    xl: 'w-10 h-10 text-base',
  };
  
  // Use external image (from API) if provided, otherwise fall back to internal icon service
  const iconUrl = externalImage || (currency?.id ? getIconUrl(currency.id) : null);
  const bgColor = getCoinColor(currency?.ticker);
  const initial = currency?.ticker?.charAt(0).toUpperCase() || '?';
  
  // If error or no icon URL, show fallback
  if (error || !iconUrl) {
    return (
      <div
        className={`
          ${sizeClasses[size]} 
          rounded-full 
          flex items-center justify-center 
          font-bold text-white 
          shadow-sm
          ${className}
        `}
        style={{ backgroundColor: bgColor }}
        title={currency?.name || currency?.ticker}
      >
        {initial}
      </div>
    );
  }
  
  return (
    <img
      src={iconUrl}
      alt={currency?.name || currency?.ticker}
      className={`
        ${sizeClasses[size]} 
        rounded-full 
        object-contain
        ${className}
      `}
      loading={lazy ? 'lazy' : 'eager'}
      onError={() => setError(true)}
      title={currency?.name}
    />
  );
};

export default CurrencyIcon;
